import {
  Goal,
  goalsArchivedSectionID,
  ID,
  rootId,
  TaskListItemTypes,
  TaskListTreeItem,
} from '@griply/types';

import { BuildGoalListTreeOptions } from './build-deadline-goal-list-tree';

export const buildNoGroupingGoalListTree = (
  goals: Goal[],
  {
    hideArchived,
    hideCompleted,
    archivedSectionName,
  }: BuildGoalListTreeOptions,
): Record<ID, TaskListTreeItem> => {
  const activeGoals: ID[] = [];
  const archivedGoals: ID[] = [];

  const goalMap = goals.reduce<Record<ID, Goal>>((acc, goal) => {
    acc[goal.id] = goal;
    return acc;
  }, {});

  const goalChildrenMap = goals.reduce<Record<ID, ID[]>>((acc, goal) => {
    acc[goal.id] = acc[goal.id] ?? [];
    const directParentId = goal.parentIds?.[goal.parentIds.length - 1];
    if (directParentId && goalMap[directParentId]) {
      acc[directParentId] = [...(acc[directParentId] ?? []), goal.id];
    }
    return acc;
  }, {});

  const treeItems: Record<ID, TaskListTreeItem> = {};

  goals.forEach((goal) => {
    if (hideArchived && goal.archivedAt) {
      return;
    }
    if (!hideArchived && goal.archivedAt) {
      archivedGoals.push(goal.id);
    }

    if (!hideCompleted || !goal.completedAt) {
      activeGoals.push(goal.id);
    }

    const children =
      goalChildrenMap[goal.id]?.filter((id) => {
        const child = goalMap[id];
        return (
          child && !child.archivedAt && (!hideCompleted || !child.completedAt)
        );
      }) ?? [];
    treeItems[goal.id] = {
      id: goal.id,
      name: goal.name,
      type: TaskListItemTypes.Goal,
      children,
      isFolder: !!children.length,
    };
  });

  if (archivedGoals.length) {
    treeItems[goalsArchivedSectionID] = {
      id: goalsArchivedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: archivedSectionName,
      children: archivedGoals,
      isFolder: true,
    };
  }

  const rootChildren = activeGoals.filter((goalId) => {
    const goal = goalMap[goalId];
    if (goal.archivedAt && !hideArchived) {
      return false;
    }
    // A goal whose parents are all absent from the tree is treated as a root.
    return goal.parentIds?.length
      ? !goal.parentIds.some((parentId) => treeItems[parentId])
      : true;
  });

  if (archivedGoals.length) {
    rootChildren.push(goalsArchivedSectionID);
  }

  return {
    [rootId]: {
      id: rootId,
      name: 'Root',
      type: TaskListItemTypes.Root,
      children: rootChildren,
    },
    ...treeItems,
  };
};
